import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { listFrom, loadErrorMessage, settledList } from '../../utils/loadState'
import { getCustomer, getDocuments, searchDocuments, uploadDocument } from '../../api'
import './Documents.css'

export default function Documents() {
  const { customerId } = useParams()
  const [customer, setCustomer] = useState(null)
  const [documents, setDocuments] = useState([])
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  // A failed load / search is an error with a Retry, never "No documents uploaded" or
  // "0 results" (item 201).
  const [docsError, setDocsError] = useState(null)
  const [searchError, setSearchError] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const [ctx, docs] = await Promise.allSettled([
        getCustomer(customerId), getDocuments(customerId),
      ])
      if (ctx.status === 'fulfilled') setCustomer(ctx.value)
      // A failed read of the document store answers an HTTP error (item 201).
      const dl = settledList(docs, 'documents')
      setDocuments(dl.items || [])
      setDocsError(dl.error)
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [customerId])

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await uploadDocument(file, customerId)
    } catch (err) { alert('Upload failed: ' + err.message); return }
    finally { setUploading(false) }
    // The upload succeeded: re-read the list through load(), so a failed re-read shows
    // as an error with a Retry, not as an empty library or an "Upload failed".
    load()
  }, [customerId])

  async function handleSearch() {
    if (!search.trim()) { setSearchResults(null); setSearchError(null); return }
    setSearchError(null)
    try {
      const results = await searchDocuments(customerId, search)
      setSearchResults(listFrom(results, 'results', 'documents'))
    } catch (err) {
      setSearchResults(null)
      setSearchError(loadErrorMessage(err))
    }
  }

  const docTypeIcons = { pdf: '📕', docx: '📘', xlsx: '📗', csv: '📊', txt: '📄' }
  function getIcon(name) {
    const ext = name?.split('.').pop()?.toLowerCase()
    return docTypeIcons[ext] || '📄'
  }

  const companyName = customer?.company_name || customerId
  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Documents' },
  ]

  if (loading) return <><TopNav breadcrumbs={breadcrumbs} /><div className="dc-loading"><div className="dc-spinner" /><p>Loading…</p></div></>

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} />
      <div className="dc-page">
        <div className="dc-header">
          <h1 className="dc-title">Documents</h1>
          <p className="dc-subtitle">{docsError ? 'Documents could not be loaded' : `${documents.length} document${documents.length !== 1 ? 's' : ''}`}</p>
        </div>

        {/* Search */}
        <div className="dc-search-row">
          <input className="dc-search" placeholder="Search document content…" value={search}
            onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} />
          <button className="dc-search-btn" onClick={handleSearch}>Search</button>
        </div>

        {searchError && <LoadError what="search results" error={searchError} onRetry={handleSearch} />}

        {searchResults && (
          <div className="dc-search-results">
            <div className="dc-results-header">
              <span>{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{search}"</span>
              <button className="dc-clear" onClick={() => setSearchResults(null)}>Clear</button>
            </div>
            {searchResults.map((r, i) => (
              <div key={i} className="dc-result-card">
                <div className="dc-result-title">{r.document_name || r.file_name || 'Document'}</div>
                <div className="dc-result-snippet">{r.content?.slice(0, 200) || r.snippet || 'No preview'}</div>
                {r.score !== undefined && <div className="dc-result-score">Relevance: {(r.score * 100).toFixed(0)}%</div>}
              </div>
            ))}
          </div>
        )}

        {/* Document List */}
        {docsError ? (
          <LoadError what="documents" error={docsError} onRetry={load} />
        ) : documents.length > 0 ? (
          <div className="dc-list">
            {documents.map((d, i) => (
              <div key={i} className="dc-card">
                <span className="dc-card-icon">{getIcon(d.file_name || d.name)}</span>
                <div className="dc-card-info">
                  <div className="dc-card-name">{d.file_name || d.name || 'Document'}</div>
                  <div className="dc-card-meta">
                    {d.page_count && `${d.page_count} pages · `}
                    {d.chunk_count && `${d.chunk_count} chunks · `}
                    {d.uploaded_at && new Date(d.uploaded_at).toLocaleDateString()}
                  </div>
                </div>
                <span className={`dc-card-status ${d.status === 'indexed' ? 'ok' : d.status === 'error' ? 'err' : 'pending'}`}>
                  {d.status || 'ready'}
                </span>
              </div>
            ))}
          </div>
        ) : !searchResults && (
          <div className="dc-empty">
            <div className="dc-empty-icon">📁</div>
            <h3>No documents uploaded</h3>
            <p>Upload policy documents, plan descriptions, or reference materials for semantic search.</p>
          </div>
        )}

        {/* Upload */}
        <div className="dc-upload-section">
          <label className="dc-upload-zone">
            <div className="dc-upload-text">{uploading ? 'Uploading…' : 'Drop files here or click to upload'}</div>
            <div className="dc-upload-hint">PDF, DOCX, XLSX, CSV, TXT</div>
            <input type="file" accept=".pdf,.docx,.xlsx,.csv,.txt" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
      </div>
    </>
  )
}
